import { z } from 'zod';

/**
 * Native (mobile) sign-in through Bunker's own Cispracs ID web flow
 * (docs/exec-plans/active/cispracs-id-rp-native.md).
 *
 *   1. The app makes `state` and a PKCE verifier, and opens in the system auth
 *      browser `/api/auth/oidc/login?returnTo=<NATIVE_SIGN_IN_DONE_PATH?state=…&challenge=…>`.
 *   2. The web sign-in sets the browser's session cookie and returns to
 *      `NATIVE_SIGN_IN_DONE_PATH`, which answers `302 bunker://auth?code=…&state=…`
 *      (or `?error=…&state=…`).
 *   3. The app posts `{ code, verifier, state }` to `NATIVE_SIGN_IN_REDEEM_PATH`
 *      with `X-Bunker-Client: native` and receives its own session tokens.
 */
export const NATIVE_SIGN_IN_DONE_PATH = '/api/auth/native/done';
export const NATIVE_SIGN_IN_REDEEM_PATH = '/api/auth/native/redeem';
/** The app's callback; registered in the Android manifest and iOS Info.plist. */
export const NATIVE_SIGN_IN_CALLBACK = 'bunker://auth';
/** A redemption code lives this long and is spent by its first redeem. */
export const NATIVE_SIGN_IN_CODE_TTL_SEC = 60;

/** Opaque, base64url, at least 128 bits. */
export const nativeSignInStateSchema = z.string().regex(/^[A-Za-z0-9_-]{22,128}$/);
/** BASE64URL(SHA-256(verifier)) without padding (RFC 7636 S256). */
export const pkceChallengeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/** RFC 7636 code_verifier: 43–128 unreserved characters. */
export const pkceVerifierSchema = z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/);
/** 32 random bytes, base64url. */
export const nativeSignInCodeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

export const nativeSignInDoneQuerySchema = z.object({
  state: nativeSignInStateSchema,
  challenge: pkceChallengeSchema,
});

export const nativeSignInRedeemBodySchema = z.object({
  code: nativeSignInCodeSchema,
  verifier: pkceVerifierSchema,
  state: nativeSignInStateSchema,
}).strict();

/** `error` values `NATIVE_SIGN_IN_DONE_PATH` sends back to the app. */
export const nativeSignInDoneErrorSchema = z.enum(['login_required', 'unsupported_session']);

export const nativeSignInRedeemResponseSchema = z.object({
  ok: z.literal(true),
  sessionToken: z.string().min(1),
  refreshToken: z.string().min(1),
  user: z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  }),
}).strict();

/** Error codes of the legacy platform-token sign-in, retired 12.10.2026. */
export const LEGACY_AUTH_RETIRED = 'legacy_auth_retired';

export type NativeSignInRedeemBody = z.infer<typeof nativeSignInRedeemBodySchema>;
export type NativeSignInRedeemResponse = z.infer<typeof nativeSignInRedeemResponseSchema>;
